import { useRouter } from "expo-router";
import { Linking, Platform, StyleSheet, View } from "react-native";
import {
  Badge,
  Button,
  Card,
  Screen,
  Text,
  useTheme,
  useThemedStyles,
} from "~/components/ui";
import { AccountData } from "~/components/AccountData";
import { useAuth } from "~/lib/auth";
import { spacing, type ThemeColors } from "~/styles/tokens";
import { notify } from "~/utils/notify";
import {
  PRO_PRICE_CURRENCY,
  PRO_PRICE_PER_MONTH,
  PRO_TRIAL_SUMMARY,
} from "~/utils/pricing";

const PRO_FEATURES = [
  "Your starter, recipes, and timers sync across every device",
  "Push notifications for feeding reminders and cook timers, even with the app closed",
];

export default function SubscriptionScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const { hasPro, purchases, user } = useAuth();

  const upgrade = async () => {
    try {
      const useNativePaywall =
        purchases.supportsNativePaywall && Platform.OS !== "web";
      if (useNativePaywall) {
        await purchases.presentPaywall();
        return;
      }
      const pkg = purchases.packages[0];
      if (!pkg) {
        notify(
          "Not available yet",
          "Subscriptions are not configured for this environment yet.",
        );
        return;
      }
      await purchases.purchase(pkg);
    } catch (error) {
      if (error && typeof error === "object" && "userCancelled" in error)
        return;
      notify(
        "Purchase failed",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  };

  const manage = async () => {
    try {
      if (purchases.supportsCustomerCenter && Platform.OS !== "web") {
        await purchases.presentCustomerCenter();
        return;
      }
      const url = await purchases.getManagementUrl();
      if (url) {
        await Linking.openURL(url);
        return;
      }
      notify(
        "Manage your subscription",
        "Use the App Store or Google Play subscription settings on the device you purchased with.",
      );
    } catch (error) {
      notify(
        "Unable to open subscription management",
        error instanceof Error ? error.message : "Please try again.",
      );
    }
  };

  return (
    <Screen scroll>
      <View style={styles.header}>
        <Text style={styles.emoji}>🍞</Text>
        <Text variant="heading" align="center">
          {hasPro ? "DoughBro Pro" : "Go Pro"}
        </Text>
        <Text variant="body" align="center" color={colors.muted}>
          {hasPro
            ? "Thanks for supporting DoughBro."
            : `${PRO_TRIAL_SUMMARY}, billed in ${PRO_PRICE_CURRENCY}.`}
        </Text>
      </View>

      <Card elevation="raised" style={styles.card}>
        <View style={styles.badgeRow}>
          <Text variant="label">Plan</Text>
          <Badge label={hasPro ? "Active" : "Free"} />
        </View>
        {PRO_FEATURES.map((feature) => (
          <View key={feature} style={styles.reasonRow}>
            <Text>{hasPro ? "✓" : "🔒"}</Text>
            <Text variant="body" style={styles.reasonText}>
              {feature}
            </Text>
          </View>
        ))}
      </Card>

      {purchases.error ? (
        <Text variant="caption" color={colors.danger} align="center">
          {purchases.error}
        </Text>
      ) : null}

      {hasPro ? (
        <Button
          label={purchases.managing ? "Opening..." : "Manage subscription"}
          onPress={() => void manage()}
          variant="secondary"
          fullWidth
        />
      ) : (
        <Button
          label={
            purchases.purchasing
              ? "Opening..."
              : `Subscribe — ${PRO_PRICE_PER_MONTH}`
          }
          onPress={() => void upgrade()}
          loading={purchases.purchasing}
          disabled={!purchases.configured && purchases.packages.length === 0}
          variant="primary"
          fullWidth
        />
      )}

      <Button
        label={purchases.loading ? "Loading..." : "Restore purchases"}
        onPress={() => void purchases.restore()}
        variant="ghost"
        fullWidth
        disabled={!purchases.configured || purchases.loading}
      />

      {user?.email ? <AccountData email={user.email} /> : null}

      <Button
        label="Back"
        onPress={() => router.back()}
        variant="ghost"
        fullWidth
      />
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    header: {
      alignItems: "center",
      paddingTop: spacing.xl,
      paddingBottom: spacing.lg,
      gap: spacing.xs,
    },
    emoji: {
      fontSize: 40,
      lineHeight: 48,
    },
    card: {
      marginBottom: spacing.lg,
      gap: spacing.sm,
    },
    badgeRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    reasonRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: spacing.sm,
    },
    reasonText: {
      flex: 1,
      color: c.heading,
    },
  });
